import {
  placeBasePath,
  SECTION_IDS,
  sectionPath,
  type AdministrationScope,
  type Place,
  type PlaceCan,
  type SectionId,
} from './place';

/**
 * What the signed-in person may do at a place, from the grants `/v1/administration/scopes` lists.
 *
 * Rights flow down (ADR-018): a global grant covers every tenant and organization, a tenant grant
 * every organization of its tenant. A place may therefore be covered by more than one grant — a
 * tenant administrator who is also an organization administrator inside the same tenant — and the
 * effective `can` is their union: each key is true when any covering grant allows it.
 *
 * This only decides what the console SHOWS. Every call names its place and the server authorizes
 * it again (CST-AD-1); a key that is wrongly true here costs a refusal, never a write.
 */

const NONE: PlaceCan = {
  browseTenants: false,
  browseOrganizations: false,
  managePeople: false,
  grantRoles: false,
  invite: false,
  deactivate: false,
  manageModules: false,
};

function union(a: PlaceCan, b: PlaceCan): PlaceCan {
  return {
    browseTenants: a.browseTenants || b.browseTenants,
    browseOrganizations: a.browseOrganizations || b.browseOrganizations,
    managePeople: a.managePeople || b.managePeople,
    grantRoles: a.grantRoles || b.grantRoles,
    invite: a.invite || b.invite,
    deactivate: a.deactivate || b.deactivate,
    manageModules: a.manageModules || b.manageModules,
  };
}

/**
 * The tenant an organization belongs to, when the client knows it: from an organization grant's
 * own `tenant` field, or from what the caller passes (the chooser list it came through, or the
 * place read once it exists).
 */
export function tenantOfOrganization(
  organizationId: string,
  scopes: readonly AdministrationScope[],
): string | null {
  for (const scope of scopes) {
    if (scope.place.kind === 'organization' && scope.place.id === organizationId) {
      return scope.tenant?.id ?? null;
    }
  }
  return null;
}

function covers(scope: AdministrationScope, place: Place, tenantId: string | null): boolean {
  if (scope.tier === 'global') {
    return true;
  }
  if (place.kind === 'platform') {
    return false;
  }
  if (scope.tier === 'tenant') {
    const grantTenant = scope.place.id;
    return place.kind === 'tenant' ? grantTenant === place.id : grantTenant === tenantId;
  }
  return place.kind === 'organization' && scope.place.id === place.id;
}

/**
 * The effective `can` at `place`, or `null` when no grant the client knows of covers it.
 *
 * `null` is not "forbidden": for an organization below a tenant grant whose tenant the client has
 * not learned yet, the server is the one that knows. The area then shows the place without
 * sections until the place read answers, rather than guessing.
 */
export function canAt(
  place: Place,
  scopes: readonly AdministrationScope[],
  knownTenantId?: string | null,
): PlaceCan | null {
  const tenantId =
    place.kind === 'organization'
      ? (knownTenantId ?? tenantOfOrganization(place.id, scopes))
      : null;
  let result: PlaceCan | null = null;
  for (const scope of scopes) {
    if (covers(scope, place, tenantId)) {
      result = union(result ?? NONE, scope.can);
    }
  }
  return result;
}

/**
 * The sections built so far. Slice 1 ships people, roles and invitations at tenants and
 * organizations; slice 2 adds the rest and replaces today's operator screens (FR-AD-12), and
 * grows this set when it does.
 */
export const AVAILABLE_SECTIONS: ReadonlySet<SectionId> = new Set<SectionId>([
  'overview',
  'tenants',
  'organizations',
  'people',
  'roles',
  'invitations',
  'modules',
  'role-catalog',
]);

/** The sections of a place for a caller who may do `can` there, in rail order (FR-AD-2). */
export function sectionsFor(
  place: Place,
  can: PlaceCan,
  available: ReadonlySet<SectionId> = AVAILABLE_SECTIONS,
): SectionId[] {
  const atTenant = place.kind === 'tenant';
  const atOrganization = place.kind === 'organization';
  const below = atTenant || atOrganization;
  const shown: Record<SectionId, boolean> = {
    // The platform has no Riepilogo in slice 2 (the contract has none); whether it gets one is the
    // Director's to decide (#214).
    overview: below,
    tenants: place.kind === 'platform' && can.browseTenants,
    organizations: atTenant && can.browseOrganizations,
    people: below && can.managePeople,
    roles: below && can.grantRoles,
    invitations: below && can.invite,
    // Read only to an organization administrator: the section shows, its buttons do not.
    modules: below,
    'role-catalog': true,
  };
  return SECTION_IDS.filter((id) => shown[id] && available.has(id));
}

/**
 * Where the area opens (FR-AD-1): on the shell's active organization when the caller administers
 * it, otherwise on the broadest place of their first grant (`/scopes` lists them broadest first).
 */
export function landingPlace(
  scopes: readonly AdministrationScope[],
  active: { readonly tenantId: string; readonly organizationId: string } | null,
): Place | null {
  if (active !== null) {
    const here: Place = { kind: 'organization', id: active.organizationId };
    if (canAt(here, scopes, active.tenantId) !== null) {
      return here;
    }
  }
  const first = scopes[0];
  if (first === undefined) {
    return null;
  }
  if (first.place.kind === 'platform') {
    return { kind: 'platform' };
  }
  return first.place.id ? { kind: first.place.kind, id: first.place.id } : null;
}

/**
 * The address that enters a place. Until slice 2 replaces today's operator screens,
 * `/admin/tenants/:id` is still the old tenant page (FR-AD-12), so a tenant is entered through
 * its first section; the platform and an organization through their own address, whose index
 * picks the first section.
 */
export function placeEntryPath(place: Place, can: PlaceCan | null): string {
  if (place.kind === 'tenant' && can !== null) {
    const first = sectionsFor(place, can)[0];
    if (first !== undefined) {
      return sectionPath(place, first);
    }
  }
  return placeBasePath(place);
}
